import React, { useState } from 'react';
import { 
  isBlankString,
  stringIsDecimal,
  formatStringToNumber } from '../../../../helpers/helpers';
import { message } from 'antd';
import { MapForm } from '../types';

type ChangeEvent = React.ChangeEvent<HTMLInputElement>;

export default ({ formState, setFormState, imageElement, saveImageToState } : MapForm) => {

  const [feetPlaceholder, setFeetPlaceholder] = useState({ width: ' ft', height: ' ft'});

  const handleInputChange = (event: ChangeEvent) => {
    setFormState({ ...formState, [event.target.id]: event.target.value });
  };

  const handleFileChange = (event: ChangeEvent) => {
    if (event.target.files && event.target.files[0]) {
      const fileSize = event.target.files[0].size / 1024 / 1024;
      if ((Math.round(fileSize * 100) / 100) <= 4) {
        saveImageToState(event.target.files[0]);
      } else {
        message.warning({
          content: 'Images must be smaller than 4mb.',
          className: 'antd-message-alert'
        });
      }
    }
  };

  const handleCoordinatesChange = (event: ChangeEvent, index: number) => {
    const areaCoords = [...formState.areaCoords];
    if (stringIsDecimal(event.target.value)) {
      const value = formatStringToNumber(event.target.value);
      areaCoords[index] = validateCoordinatesWithinImage(+value, index);
    } else if (event.target.value === '') {
      areaCoords[index] = 0;
    }
    setFormState({ ...formState, areaCoords });
  };

  const validateCoordinatesWithinImage = (coordinate: number, index: number) => {
    const { naturalWidth, naturalHeight } = imageElement.current as HTMLImageElement;
    if (index === 0 || index === 2) {
      if (coordinate > naturalWidth) return naturalWidth;
    } else {
      if (coordinate > naturalHeight) return naturalHeight;
    }
    return coordinate;
  };

  const handleAreaFeetDimensionsChange = (event: ChangeEvent, index: number) => {
    if (!isNaN(event.target.value as any) || event.target.value === '') {
      const areaFeetDimensions = [...formState.areaFeetDimensions];
      const value = formatStringToNumber(event.target.value, 2);
      areaFeetDimensions[index] = value ? value : 0;
      setFormState({ ...formState, areaFeetDimensions });
    }
  };

  const handleLotLangChange = (event: ChangeEvent) => {
    const isNegativeSign = event.target.value[0] === '-' && event.target.value.length === 1;
    if ((!isNaN(event.target.value as any) || isNegativeSign) && event.target.value !== '') {
      const value = isNegativeSign ? '-' : formatStringToNumber(event.target.value);
      setFormState({ ...formState, [event.target.id]: value });
    } else if (event.target.value === '') {
      setFormState({ ...formState, [event.target.id]: '' });
    }
  };

  const { areaName, areaCoords, lat, lng, type, areaFeetDimensions, fileName = '' } = formState;
  const [x0, y0, x1, y1] = areaCoords;
  const fileLoaded = !isBlankString(fileName);

  return (
    <>
      <div className="material-textbox" id="first-name-input">
        <input
          required
          id="areaName"
          value={areaName}
          className="material-input"
          onChange={handleInputChange}
        />
        <span
          className="material-label"
        >Name</span>
      </div>
      <div className="custom-file material-textbox">
        <input
          type="file"
          className="custom-file-input"
          id="inputGroupFile01"
          aria-describedby="inputGroupFileAddon01"
          onChange={handleFileChange}
        />
        <label
          className="custom-file-label material-input"
          htmlFor="inputGroupFile01"
          style={fileLoaded ? { color: 'black' } : { color: '$gray-bg' }}
        >
          {fileLoaded ? fileName : 'Upload Practice Field Image'}
        </label>
      </div>
      <div className="custom-practice-area-types">
        <div className="check-01 radio header-cc">
          <input
            id="radio-button-All"
            type="radio"
            checked={type === 'indoor'}
            onChange={() => setFormState({ ...formState, type: 'indoor' })}
          />
          <label htmlFor="radio-button-All" className="radio-label capitalize-text">Indoor</label>
          <input
            id="radio-button-practices"
            type="radio"
            checked={type === 'outdoor'}
            onChange={() => setFormState({ ...formState, type: 'outdoor' })}
          />
          <label
            htmlFor="radio-button-practices" className="radio-label capitalize-text"
          >Outdoor</label>
        </div>
      </div>
      <div className="img-form">
        <div className="material-textbox">
          <input
            id="x0"
            className="material-input"
            value={x0}
            required
            onChange={e => handleCoordinatesChange(e, 0)}
          />
          <span className="material-label">X0</span>
        </div>
        <div className="material-textbox">
          <input
            id="y0"
            className="material-input"
            value={y0}
            required
            onChange={e => handleCoordinatesChange(e, 1)}
          />
          <span className="material-label">Y0</span>
        </div>
        <div className="material-textbox">
          <input
            id="x1"
            className="material-input"
            value={x1}
            required
            onChange={e => handleCoordinatesChange(e, 2)}
          />
          <span className="material-label">X1</span>
        </div>
        <div className="material-textbox">
          <input
            id="y1"
            className="material-input"
            value={y1}
            required
            onChange={e => handleCoordinatesChange(e, 3)}
          />
          <span className="material-label">Y1</span>
        </div>
      </div>
      <div className="img-form-00">
        <div className="material-textbox" style={{ marginRight: '7.5px' }}>
          <input
            id="widthFt"
            className="material-input"
            required
            value={areaFeetDimensions[0] + feetPlaceholder.width}
            onFocus={() => setFeetPlaceholder({ ...feetPlaceholder, width: '' })}
            onBlur={() => setFeetPlaceholder({ ...feetPlaceholder, width: ' ft' })}
            onChange={e => handleAreaFeetDimensionsChange(e, 0)}
          />
          <span className="material-label">Area Width</span>
        </div>
        <div className="material-textbox" style={{ marginLeft: '7.5px' }}>
          <input
            id="heightFt"
            className="material-input"
            value={areaFeetDimensions[1] + feetPlaceholder.height}
            onFocus={() => setFeetPlaceholder({ ...feetPlaceholder, height: '' })}
            onBlur={() => setFeetPlaceholder({ ...feetPlaceholder, height: ' ft' })}
            onChange={e => handleAreaFeetDimensionsChange(e, 1)}
            required
          />
          <span className="material-label">Area Height</span>

        </div>
      </div>
      <div className="img-form-00">
        <div className="material-textbox" style={{ marginRight: '7.5px' }}>
          <input
            className="material-input no-asterisk"
            id="lng"
            value={lng}
            onChange={event => handleLotLangChange(event)}
          />
          <span className="material-label">Longitude</span>
        </div>
        <div className="material-textbox" style={{ marginLeft: '7.5px' }}>
          <input
            id="lat"
            value={lat}
            className="material-input no-asterisk"
            onChange={event => handleLotLangChange(event)}
          />
          <span className="material-label">Latitude</span>
        </div>
      </div>
    </>
  );
};
